/**
 * Переключатель из нескольких взаимоисключающих вариантов.
 * В макете используется для режима привязки проекта (BOARD / COLUMN)
 * и периодов отчётности (неделя / месяц / произвольный).
 */

import { Radio, RadioGroup } from "@headlessui/react";

import { cn } from "./cn";

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentOption<T>[];
  /** Доступное название группы — обязательно, иначе набор кнопок безымянен. */
  label: string;
  size?: "sm" | "md";
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
  size = "md",
}: SegmentedControlProps<T>) {
  return (
    <RadioGroup
      value={value}
      onChange={onChange}
      aria-label={label}
      // Не шире родителя: на телефоне лишние сегменты прокручиваются,
      // а не раздвигают страницу вбок.
      className="inline-flex max-w-full overflow-x-auto rounded-md border border-line bg-sunken p-0.5 [scrollbar-width:none]"
    >
      {options.map((option) => (
        <Radio
          key={option.value}
          value={option.value}
          {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
          className={cn(
            "cursor-pointer rounded-[6px] font-semibold whitespace-nowrap transition-colors",
            size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-[13px]",
            "text-muted hover:text-fg",
            "data-checked:bg-surface data-checked:text-fg data-checked:shadow-sm",
            "data-disabled:cursor-not-allowed data-disabled:opacity-50",
          )}
        >
          {option.label}
        </Radio>
      ))}
    </RadioGroup>
  );
}
